<template>
	<div>
		<JonInfo class="mb-4">
			<template #message>
				<p>
					Gib eine Lebensstation ein, wie Job, Auszeit, Karenz oder ehrenamtliche Tätigkeit.<br/>
					<b>Deine Texteingaben sind erst dann für einen bestimmten Arbeitgeber sichtbar, wenn du den Kontakt
						zu ihm bestätigt hast.</b>
				</p>
			</template>
		</JonInfo>
		<div class="sections">
			<section>
				<h3>{{ titles.startEnd }}</h3>
				<DateEdit
					v-model="startEnd"
					@input="emitChangeEvent"
				/>
			</section>
			<section>
				<h3>{{ titles.careerType }}</h3>
				<Select
					v-model="careerType"
					:options="careerTypeOptions"
					class="career-type-select"
					@change="careerTypeChanged"
				/>
			</section>
			<template v-if="careerType">
				<b-row v-if="companyNameEligible || companyWebsiteEligible">
					<b-col v-if="companyNameEligible" md="8">
						<section class="first-level-section">
							<h3 v-if="computedCompanyNameTitle">{{ computedCompanyNameTitle }}</h3>
							<h3 v-else>Name des Arbeitgebers <span class="sub">(Organisation/Unternehmen)</span></h3>
							<b-input v-model="companyName" maxlength="250" @input="emitChangeEvent"/>
							<LockInfo class="mt-2">
								Wird einem Arbeitgeber erst dann angezeigt, wenn du deinen Lebenslauf zur Ansicht
								freigibst.
							</LockInfo>
						</section>
					</b-col>
					<b-col v-if="companyWebsiteEligible" md="4">
						<section class="first-level-section">
							<h3>Website <span class="sub">(optional)</span></h3>
							<FormGroup>
								<b-input v-model="companyWebsite" maxlength="250" @input="emitChangeEvent"/>
							</FormGroup>
						</section>
					</b-col>
				</b-row>
				<section v-if="titleEligible">
					<h3>{{ computedTitleTitle }}</h3>
					<b-input v-model="title" maxlength="250" @input="emitChangeEvent"/>
				</section>
				<section>
					<h3>{{ computedDescriptionTitle }}</h3>
					<p
						v-if="isDescriptionMandatory"
						class="d-inline-block color-turquoise font-weight-extra-bold"
						v-b-tooltip.html
						:title="descriptionPlaceholder"
					>
						Gib alle Details an, das erhöht deine Chancen!
						<Icon icon="info" color="turquoise" size="1comma5x" class="ml-1"/>
					</p>
					<Wysiwyg
						v-model="description"
						:placeholder="descriptionPlaceholder"
						@input="emitChangeEvent"
					/>
					<div v-if="descriptionError" class="error font-size-smaller mt-1">{{ descriptionError }}</div>
				</section>
				<template v-if="careerTypeJob">
					<section>
						<h3>
							{{ titles.careerLevel }}
							<span v-if="!isCareerLevelMandatory">(optional)</span>
						</h3>
						<CharacteristicEdit
							v-model="careerLevel"
							:edit-config="{
								title: titles.careerLevel,
								characteristicItems: meta.careerLevel,
								characteristicType: meta.careerLevelType,
								singleChoice: true
							}"
							open-fan-label="Berufsebene wählen"
							@input="emitChangeEvent"
						/>
					</section>
					<section>
						<h3>
							{{ titles.functions }}
							<span v-if="!isFunctionsMandatory">(optional)</span>
						</h3>
						<CharacteristicEdit
							v-model="functions"
							:edit-config="{
								title: titles.functions,
								characteristicItems: meta.function,
								characteristicType: meta.functionType,
								supplementsConfig: {
									nothingAllowed: false,
									types: functionSupplementTypes
								},
								withKeyword: true,
								searchConfig: {
									catalog: meta.searchCatalogFunction
								}
							}"
							open-fan-label="Tätigkeit(en) wählen"
							@input="functionsChanged"
						/>
					</section>
					<section v-if="isIndustrialSectorEligible">
						<h3>
							{{ titles.industrialSectors }}
							<span v-if="!isIndustrialSectorsMandatory">(optional)</span>
						</h3>
						<CharacteristicEdit
							v-model="industrialSectors"
							:edit-config="{
								title: titles.industrialSectors,
								characteristicItems: meta.industrialSector,
								characteristicType: meta.industrialSectorType,
								withKeyword: true,
								searchConfig: {
									catalog: meta.searchCatalogIndustrialSector
								}
							}"
							open-fan-label="Branche(n) wählen"
							@input="emitChangeEvent"
						/>
					</section>
					<section v-if="isOrganisationAreaEligible">
						<h3>{{ titles.organisationalArea }}</h3>
						<CharacteristicEdit
							v-model="organisationalArea"
							:edit-config="{
								title: titles.organisationalArea,
								characteristicItems: organisationalAreaItems,
								characteristicType: meta.organisationalAreaType,
								supplementsConfig: {
									nothingAllowed: false
								},
								withKeyword: true,
							}"
							open-fan-label="Organisationsbereich(e) wählen"
							@input="emitChangeEvent"
						/>
					</section>
				</template>
			</template>
		</div>
	</div>
</template>

<script>
import FormGroup from "@/vue/common/form/Group.vue";
import Select from "@/vue/common/form/Select";
import _ from "lodash";
import * as CharacteristicTag from "@/helper/characteristic/Tag";
import CharacteristicEdit from "@/vue/application/career/CharacteristicEdit"
import DateEdit from "./DateEdit"
import {isDefined} from "@/helper/Value";
import Wysiwyg from "@/vue/common/form/Wysiwyg";
import {isEmptyOrNull, stringOrNull, stripTags} from "@/helper/String";
import {filterLastLevel} from "@/helper/characteristic/Characteristic";
import {uuid} from "vue-uuid";
import {APPLICATION, get} from "@/helper/characteristic/Label";
import {FUNCTION_SCOPE} from "@/helper/characteristic/Type";
import {characteristicMultiple} from "@/helper/application/Populate";
import JonInfo from "@/vue/common/JonInfo.vue";
import Icon from "@/vue/icon/Icon.vue";
import * as CareerType from "@/helper/characteristic/CareerType";
import {isTitleEligible} from "@/helper/characteristic/CareerType";
import {isEmptyArray} from "@/helper/Array";
import {clearSelected, filterItems, isEligible} from "@/helper/characteristic/OrganisationalArea";
import {RELEVANT_SUPPLEMENT_TYPES} from "@/helper/characteristic/Functions";
import {isSuppress} from "@/helper/characteristic/IndustrialSector";
import LockInfo from "@/vue/common/LockInfo.vue";

const SPECIAL_COMPANY_NAME = {
	[CareerType.FREELANCE]: 'Name des Unternehmens/der Organisation',
	[CareerType.SEASONAL_WORK]: 'Name des Arbeitgebers (optional)',
	[CareerType.HONORARY_POST]: 'Name des Arbeitgebers (optional)',
};

const SPECIAL_TITLE = {
	[CareerType.SABBATICAL]: 'Welche Art der Auszeit hattest Du? – Zum Beispiel: Bildungskarenz, Weltreise, Sabbatical, Neu-Orientierung, usw.',
	[CareerType.CIVILIAN_MILITARY_SERVICE]: 'Bezeichnung der Tätigkeit (optional)',
	[CareerType.MATERNITY_LEAVE]: 'Welche Art der Auszeit/Karenz hattest du? (optional)',
};

const SPECIAL_DESCRIPTION = {
	[CareerType.SABBATICAL]: 'Details (optional)',
	[CareerType.CIVILIAN_MILITARY_SERVICE]: 'Details (optional)',
	[CareerType.MATERNITY_LEAVE]: 'Details (optional)',
	[CareerType.PENSION]: 'Details (optional)',
	[CareerType.SEEKING_WORK]: 'Details (wenn du möchtest, kannst du hier Details angeben)',
};

export default {

	components: {
		LockInfo,
		Icon,
		JonInfo,
		FormGroup,
		Wysiwyg,
		Select,
		DateEdit,
		CharacteristicEdit,
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		meta: {
			type: Object,
			required: true,
		},
	},

	mounted ()
	{
		this.emitChangeEvent();
	},

	data ()
	{
		let startEnd = undefined;
		let careerLevel = undefined;

		if (this.value && isDefined(this.value?.start))
		{
			startEnd = {
				start: this.value?.start,
				end: this.value?.end,
			};
		}

		if (this.value?.careerLevel)
		{
			careerLevel = characteristicMultiple(
				[{item: this.value.careerLevel}],
				this.meta,
				this.meta.careerLevel,
				this.meta.careerLevelType,
				true
			);
		}

		let functions = this.value?.functions;

		if (functions)
		{
			functions = characteristicMultiple(
				this.value.functions,
				this.meta,
				this.meta.function,
				this.meta.functionType,
				true
			);
		}

		let industrialSectors = this.value?.industrialSectors;

		if (industrialSectors)
		{
			industrialSectors = characteristicMultiple(
				this.value.industrialSectors,
				this.meta,
				this.meta.industrialSector,
				this.meta.industrialSectorType,
				true
			);
		}

		let organisationalArea = this.value?.organisationalArea;

		if (organisationalArea)
		{
			organisationalArea = characteristicMultiple(
				this.value.organisationalArea,
				this.meta,
				this.meta.organisationalArea,
				this.meta.organisationalAreaType,
				true
			);
		}

		return {
			id: this.value?.id || uuid.v4(),
			startEnd,
			title: this.value?.title || '',
			description: this.value?.description || '',
			careerType: this.value?.careerType,
			careerLevel,
			functions,
			industrialSectors,
			organisationalArea,
			companyName: this.value?.companyName || '',
			companyWebsite: this.value?.companyWebsite || ''
		};
	},

	computed: {
		titles ()
		{
			return {
				careerType: 'Art der Lebenssituation',
				startEnd: 'Zeitraum',
				title: 'Jobbezeichnung: wie wurde deine Tätigkeit genannt?',
				description: 'Beschreibe deinen Job und die Tätigkeiten genauer',
				industrialSectors: 'Branche(n) deines Arbeitgebers',
				careerLevel: 'Berufsebene(n) deines Jobs',
				functions: 'Tätigkeiten deines Jobs',
				organisationalArea: 'Organisationsbereiche deines Jobs',
				companyName: 'Name des Arbeitgebers (Organisation/Unternehmen)'
			};
		},
		titleEligible ()
		{
			return !this.careerType || isTitleEligible(this.careerType);
		},
		isTitleMandatory ()
		{
			if (!this.careerType)
			{
				return false;
			}

			return CareerType.isTitleMandatory(this.careerType);
		},
		computedTitleTitle ()
		{
			if (!this.careerType)
			{
				return this.titles.title;
			}

			return SPECIAL_TITLE[this.careerType.naturalId] ?? this.titles.title;
		},
		computedCompanyNameTitle ()
		{
			return SPECIAL_COMPANY_NAME[this.careerType.naturalId] ?? this.titles.companyName;
		},
		computedDescriptionTitle ()
		{
			if (!this.careerType)
			{
				return this.titles.description;
			}

			return SPECIAL_DESCRIPTION[this.careerType.naturalId] ?? this.titles.description;
		},
		isDescriptionMandatory ()
		{
			if (!this.careerType)
			{
				return false;
			}

			return CareerType.isDescriptionMandatory(this.careerType);
		},
		descriptionPlaceholder ()
		{
			return '<div class="mb-2">Erzeuge das Interesse von ArbeitgeberInnen:</div>' +
				'<ul>' +
				'<li>beschreibe die <b>Teile und Einzelheiten</b> deiner Aufgabe</li>' +
				'<li>hebe <b>Erfolge</b> hervor</li>' +
				'<li>je <i>mehr</i>, desto besser</li>' +
				'</ul>';
		},
		functionSupplementTypes ()
		{
			return _.concat(
				RELEVANT_SUPPLEMENT_TYPES,
				FUNCTION_SCOPE
			);
		},
		careerTypeJob ()
		{
			return isDefined(this.careerType) && CharacteristicTag.hasTag(this.careerType, CharacteristicTag.CAREER_JOB);
		},
		careerTypeOptions ()
		{
			// can only be job and simple, so group here, not outside
			let forJob = _.filter(
				this.meta.careerType,
				careerType => CharacteristicTag.hasTag(careerType, CharacteristicTag.CAREER_JOB)
			);

			let forSimple = _.filter(
				this.meta.careerType,
				careerType => !CharacteristicTag.hasTag(careerType, CharacteristicTag.CAREER_JOB)
					&& !CharacteristicTag.hasTag(careerType, CharacteristicTag.CAREER_EDUCATION)
			);

			let options = [
				{value: 'job', text: 'Berufstätigkeiten', disabled: true}
			];

			forJob = _.orderBy(forJob, ['label'], ['asc']);

			_.forEach(forJob, careerType =>
			{
				options.push({
					value: careerType,
					text: get(careerType, APPLICATION)
				});
			});

			options.push(
				{value: 'job', text: 'Weitere / Sonstige Lebensstationen', disabled: true}
			);

			forSimple = _.orderBy(forSimple, ['label'], ['asc']);

			_.forEach(forSimple, careerType =>
			{
				options.push({
					value: careerType,
					text: get(careerType, APPLICATION)
				});
			});

			return options;
		},
		organisationalAreaItems ()
		{
			if (!isDefined(this.functions) || isEmptyArray(this.functions))
			{
				return this.meta.organisationalArea;
			}

			return filterItems(this.meta, this.functions);
		},
		companyNameEligible ()
		{
			return this.careerTypeJob;
		},
		companyWebsiteEligible ()
		{
			return this.careerTypeJob;
		},
		companyNameMandatory ()
		{
			return this.careerType && CareerType.isCompanyNameMandatory(this.careerType);
		},
		companyNameValid ()
		{
			if (!this.companyNameMandatory)
			{
				return true;
			}

			return !this.companyNameEligible || !isEmptyOrNull(this.companyName);
		},
		descriptionError ()
		{
			if (!this.isDescriptionMandatory)
			{
				return undefined;
			}

			if (!isEmptyOrNull(this.description) && stripTags(this.description).length < 10)
			{
				return 'Bitte gib mindestens 10 Zeichen ein';
			}

			return undefined;
		},
		descriptionValid ()
		{
			if (!this.isDescriptionMandatory)
			{
				return true;
			}

			return !isEmptyOrNull(this.description) && !this.descriptionError;
		},
		isIndustrialSectorsMandatory ()
		{
			return this.careerTypeJob
				&& CareerType.isIndustrialSectorsMandatory(this.careerType)
				&& this.isIndustrialSectorEligible;
		},
		isCareerLevelMandatory ()
		{
			return this.careerTypeJob && CareerType.isCareerLevelMandatory(this.careerType);
		},
		isFunctionsMandatory ()
		{
			return this.careerTypeJob && CareerType.isFunctionsMandatory(this.careerType);
		},
		isOrganisationAreaEligible ()
		{
			return this.functions && isEligible(
				this.functions.map(item => item.item)
			);
		},
		isIndustrialSectorEligible ()
		{
			return !isSuppress(this.meta, this.functions);
		},
		isOrganisationAreaMandatory ()
		{
			if (!this.isOrganisationAreaEligible)
			{
				return false;
			}

			return this.careerTypeJob && CareerType.isOrganisationAreaMandatory(this.careerType);
		},
		errors ()
		{
			let errors = [];

			if (!isDefined(this.startEnd))
			{
				errors.push(this.titles.startEnd);
			}

			if (this.isTitleMandatory && isEmptyOrNull(this.title))
			{
				errors.push(this.computedTitleTitle);
			}

			if (!this.careerTypeJob)
			{
				return errors;
			}

			if (!this.descriptionValid)
			{
				errors.push(this.titles.description);
			}

			if (
				this.isIndustrialSectorsMandatory
				&& (!isDefined(this.industrialSectors) || isEmptyArray(this.industrialSectors))
			)
			{
				errors.push(this.titles.industrialSectors);
			}

			if (
				this.isCareerLevelMandatory
				&& (!isDefined(this.careerLevel) || isEmptyArray(this.careerLevel))
			)
			{
				errors.push(this.titles.careerLevel);
			}

			if (
				this.isFunctionsMandatory
				&& (!isDefined(this.functions) || isEmptyArray(this.functions))
			)
			{
				errors.push(this.titles.functions);
			}

			if (
				this.isOrganisationAreaMandatory
				&& (!isDefined(this.organisationalArea) || isEmptyArray(this.organisationalArea))
			)
			{
				errors.push(this.titles.organisationalArea);
			}

			if (!this.companyNameValid)
			{
				errors.push(this.computedCompanyNameTitle);
			}

			return errors;
		},
		dataValid ()
		{
			return this.errors.length === 0;
		},
	},

	methods: {
		functionsChanged ()
		{
			if (isDefined(this.organisationalArea))
			{
				if (!this.isOrganisationAreaEligible)
				{
					this.organisationalArea = undefined;
				}
				else
				{
					this.organisationalArea = clearSelected(this.organisationalArea, this.meta, this.functions);
				}
			}

			this.emitChangeEvent();
		},
		careerTypeChanged (value)
		{
			this.careerType = undefined;

			// force re-render to not cause WYSIWYG problems
			this.$nextTick(() =>
			{
				this.careerType = value;

				this.emitChangeEvent();
			});
		},
		emitChangeEvent ()
		{
			let careerLevel = undefined;

			if (this.careerTypeJob && this.careerLevel)
			{
				careerLevel = _.first(filterLastLevel(this.careerLevel)).item;
			}

			this.$emit('input', {
				id: this.id,
				start: this.startEnd?.start,
				end: this.startEnd?.end,
				careerType: this.careerType,
				functions: this.careerTypeJob
					? this.functions
					: undefined,
				careerLevel,
				industrialSectors: this.careerTypeJob && this.isIndustrialSectorEligible
					? this.industrialSectors
					: undefined,
				organisationalArea: this.isOrganisationAreaEligible
					? this.organisationalArea
					: undefined,
				title: this.titleEligible
					? stringOrNull(this.title)
					: undefined,
				description: stringOrNull(this.description),
				companyName: this.companyNameEligible
					? this.companyName
					: undefined,
				companyWebsite: this.companyWebsiteEligible
					? stringOrNull(this.companyWebsite)
					: undefined,
				valid: this.dataValid,
				errors: this.errors,
			});
		}
	}
}
</script>
